import { FC, useMemo } from 'react';
import styles from './styles.module.scss';
import Button from '@components/common/button';
import { useLocales } from '@hooks/helpers/useLocales';
import InfoBlock from '@components/common/info_block';
import { pageAnimation } from '@const/animation';
import { motion } from 'framer-motion';
import { EFundsOperation } from '@enum';
import { useFormContext, useWatch } from 'react-hook-form';
import { TContact, TPayee } from '@xeppt/xeppt-sdk/types';
import { useUserContext } from '@hooks/context/useUserContext';

import { getFrequencyList } from '@utils/billing';
import { useTranslation } from 'react-i18next';
import { formatDate, getScheduleDescription, prettifyCardStars, prettifyStars } from '@utils/index';
import { useApiQuery } from '@hooks/api/useApiQuery';
import { apiAccountService } from '@api';
import { paymentsConfig } from '@const/payment';
import Typography from '@components/common/typography';
import { Icon } from '@components/icons';
import { useFrequency } from '@hooks/helpers/useFrequency';

interface IProps {
    onSubmit: () => void;
    onPrev: () => void;
    isLoading: boolean;
    isEdit?: boolean;
    method: Exclude<EFundsOperation, EFundsOperation.CARD_DEBIT_DEPOSIT>;
    contactList?: TContact[];
    payeesList?: TPayee[];
    isRequest?: boolean;
    isCustomContact?: boolean;
    giftCardEmails?: string[];
}

const Summary: FC<IProps> = ({
    onSubmit,
    onPrev,
    isLoading,
    isEdit,
    method,
    contactList,
    isRequest,
    payeesList,
    isCustomContact,
    giftCardEmails
}) => {
    const { submitLocale } = useLocales();
    const { getValues, watch, control, setValue } = useFormContext();
    const { account } = useUserContext();
    const isSchedule = useWatch({ name: 'isSchedule', control });
    const { t: frequencyLocale } = useTranslation('sections', {
        keyPrefix: 'send_and_request.schedule_payments.payment_details'
    });
    const { giftCardFeePoints } = useFrequency({ method });

    const isPayBills = useMemo(() => method === EFundsOperation.PAY_BILL_CREDIT_PAYMENT, [method]);

    const isBank = useMemo(
        () =>
            method === EFundsOperation.EFT_CREDIT_WITHDRAWAL ||
            method === EFundsOperation.EFT_DEBIT_DEPOSIT,
        [method]
    );

    const { data: pointsFeeData, isLoading: isFeeLoading } = useApiQuery({
        method: () => {
            return apiAccountService.calculateLoyaltyFee({
                ...paymentsConfig[method],
                fee_included: false,
                amount: Number(getValues()?.amount) || 0
            });
        },
        onSuccess: (data) => {
            setValue('amount_result', data.total);
            setValue('amount', data.amount);
        },
        isInitialRequest: false,
        isInitialLoading: true,
        deps: [method],
        condition: !!method && method !== EFundsOperation.GIFT_CARD_CREDIT_GIFT_CARD
    });

    const pointsFeeResult = useMemo(() => {
        if (method === EFundsOperation.GIFT_CARD_CREDIT_GIFT_CARD) {
            return giftCardFeePoints;
        } else {
            return pointsFeeData;
        }
    }, [pointsFeeData, giftCardFeePoints]);

    const data = useMemo(() => {
        const values = getValues();
        const foundContact = contactList?.find((item) => item.id === values.contact);
        const foundPayee = payeesList?.find((item) => item.id === values.account);
        const foundAccount = account?.bank_accounts?.find((item) => item.id === values.account);
        const foundCard = account?.eps_cards?.find((item) => item.id === values.account);
        return {
            [EFundsOperation.WALLET_CREDIT_TRANSFER]: [
                {
                    label: 'Recipient',
                    value: isCustomContact
                        ? `${getValues().new_contact.first_name} ${getValues().new_contact.last_name}`
                        : `${foundContact?.first_name} ${foundContact?.last_name}`
                },
                (foundContact?.email || isCustomContact) && {
                    label: 'Recipient’s email',
                    value: isCustomContact ? getValues().new_contact.email : foundContact?.email
                }
            ],
            [EFundsOperation.E_TRANSFER_CREDIT_TRANSFER]: [
                {
                    label: 'Recipient',
                    value: `${foundContact?.first_name} ${foundContact?.last_name}`
                },
                foundContact?.email && {
                    label: 'Recipient’s email',
                    value: foundContact?.email
                },
                foundContact?.phone && {
                    label: 'Recipient’s phone',
                    value: `+1${foundContact?.phone}`
                },
                values.security_question && {
                    label: 'Security question',
                    value: values.security_question
                },
                values.security_answer && {
                    label: 'Security answer',
                    value: values.security_answer
                }
            ],
            [EFundsOperation.EFT_CREDIT_WITHDRAWAL]: [
                {
                    label: 'Institution name',
                    value: foundAccount?.institution_name
                },
                {
                    label: 'Account nickname',
                    value: foundAccount?.account_name
                },
                {
                    label: 'Account number',
                    value: prettifyStars(foundAccount?.account_number)
                }
            ],
            [EFundsOperation.EPS_DEBIT_DEPOSIT]: [
                {
                    label: 'cardholder',
                    value: foundCard?.card_holder_name
                },
                {
                    label: 'Card brand',
                    value: foundCard?.card_brand
                },
                {
                    label: 'Card number',
                    value: prettifyCardStars(foundCard?.card_number)
                }
            ],
            [EFundsOperation.WALLET_DEBIT_REQUEST]: [
                {
                    label: 'Recipient',
                    value: isCustomContact
                        ? `${getValues().new_contact.first_name} ${getValues().new_contact.last_name}`
                        : `${foundContact?.first_name} ${foundContact?.last_name}`
                },
                (foundContact?.email || isCustomContact) && {
                    label: 'Recipient’s email',
                    value: isCustomContact ? getValues().new_contact.email : foundContact?.email
                }
            ],
            [EFundsOperation.EFT_DEBIT_DEPOSIT]: [
                {
                    label: 'Institution name',
                    value: foundAccount?.institution_name
                },
                {
                    label: 'Account nickname',
                    value: foundAccount?.account_name
                },
                {
                    label: 'Account number',
                    value: prettifyStars(foundAccount?.account_number)
                }
            ],
            [EFundsOperation.E_TRANSFER_DEBIT_REQUEST]: [
                {
                    label: 'Name',
                    value: `${foundContact?.first_name} ${foundContact?.last_name}`
                },
                foundContact?.email && {
                    label: 'Email',
                    value: foundContact?.email
                },
                foundContact?.phone && {
                    label: 'Phone',
                    value: `+${foundContact?.phone}`
                },
                values.security_question && {
                    label: 'Security question',
                    value: values.security_question
                },
                values.security_answer && {
                    label: 'Security answer',
                    value: values.security_answer
                }
            ],
            [EFundsOperation.PAY_BILL_CREDIT_PAYMENT]: [
                {
                    label: 'Payee name',
                    value: foundPayee?.name
                },
                {
                    label: 'Payee account number',
                    value: prettifyStars(foundPayee?.account_number)
                },
                {
                    label: 'Payee nickname',
                    value: foundPayee?.alias
                }
            ],
            [EFundsOperation.GIFT_CARD_CREDIT_GIFT_CARD]: [
                {
                    label: values?.isBulkGiftCards ? 'Recipients' : 'Recipient',
                    value:
                        values?.giftCardRecipientEmail ||
                        (values?.phone_code && values?.phone_number)
                            ? values?.giftCardRecipientEmail ||
                              `${values?.phone_code.includes('+') ? values?.phone_code : '+' + values?.phone_code} ${values?.phone_number}`
                            : (giftCardEmails?.length || 0) > 0
                              ? 'From uploaded CSV'
                              : '-'
                }
            ],
            [EFundsOperation.WALLET_DEBIT_TRANSFER]: [],
            [EFundsOperation.WALLET_CREDIT_REQUEST]: [],
            [EFundsOperation.WALLET_CREDIT_CARD_DEPOSIT]: [],
            [EFundsOperation.WALLET_CREDIT_CARD_SERVICE]: [],
            [EFundsOperation.CARD_CREDIT_WITHDRAWAL]: [],
            [EFundsOperation.CARD_DEBIT_DEPOSIT]: [],
            [EFundsOperation.E_TRANSFER_DEBIT_TRANSFER]: [],
            [EFundsOperation.GIFT_CARD_CREDIT_RESERVE]: [],
            [EFundsOperation.GIFT_CARD_DEBIT_GIFT_CARD_REDEEM]: [],
            [EFundsOperation.GIFT_CARD_DEBIT_RESERVE]: []
        };
    }, [watch(), contactList, payeesList, account, method]);

    const scheduleData = useMemo(() => {
        const values = getValues();
        return {
            title: 'Schedule details',
            rows: [
                {
                    label: 'Start date',
                    value: formatDate(values.next_payment_at, 'D MMMM YYYY')
                },
                {
                    label: 'Frequency',
                    value: `${
                        getFrequencyList(frequencyLocale).find(
                            (item) => item.value === values.paymentFrequency
                        )?.label
                    } (${getScheduleDescription(values.next_payment_at, values.paymentFrequency)})`
                },
                {
                    label: 'End date',
                    value: values.isInfinitePayment
                        ? 'Indefinite (until canceled)'
                        : formatDate(values.paymentsEndDate, 'D MMMM YYYY')
                }
            ]
        };
    }, [watch()]);

    const methodText = useMemo(() => {
        switch (method) {
            case EFundsOperation.WALLET_CREDIT_TRANSFER:
                return 'send';
            case EFundsOperation.WALLET_DEBIT_REQUEST:
                return 'request';
            case EFundsOperation.EFT_CREDIT_WITHDRAWAL:
                return 'withdraw';
            case EFundsOperation.EFT_DEBIT_DEPOSIT:
                return 'request';
            case EFundsOperation.E_TRANSFER_CREDIT_TRANSFER:
                return 'send';
            case EFundsOperation.E_TRANSFER_DEBIT_REQUEST:
                return 'request';
            case EFundsOperation.EPS_DEBIT_DEPOSIT:
                return 'request';
            case EFundsOperation.PAY_BILL_CREDIT_PAYMENT:
                return 'send';
        }
    }, [method]);

    const paymentData = useMemo(() => {
        const values = getValues();
        if (method === EFundsOperation.GIFT_CARD_CREDIT_GIFT_CARD) {
            return {
                title: 'Gift card details',
                rows: [
                    {
                        label: 'Gift card value (will be reserved upon activation)',
                        value: `$${values?.amount} CAD`,
                        isBold: true
                    },
                    values?.isBulkGiftCards && {
                        label: 'Quantity of gift cards',
                        value: values?.giftCardCount,
                        isBold: false
                    },
                    values?.isBulkGiftCards && {
                        label: 'Total gift card value',
                        value: `$${((values?.amount || 0) * (values?.giftCardCount || 0)).toFixed(2)} CAD`,
                        isBold: true
                    },
                    {
                        label: values?.isBulkGiftCards
                            ? 'Issuing fee (per gift card)'
                            : 'Issuing fee',
                        value: `$${(pointsFeeResult?.fee || 0).toFixed(2)} СAD`
                    },
                    values?.isBulkGiftCards && {
                        label: 'Total issuing fee',
                        value: `$${((pointsFeeResult?.fee || 0) * (values?.giftCardCount || 0)).toFixed(2)} CAD`,
                        isBold: false
                    },
                    {
                        label: 'Description',
                        value: values?.message || '-',
                        isBold: false
                    },
                    !!pointsFeeResult?.points && {
                        label: `Points you'll earn`,
                        value: pointsFeeResult?.points.toFixed(2)
                    }
                ]
            };
        }
        return {
            title:
                method === EFundsOperation.EFT_DEBIT_DEPOSIT ||
                method === EFundsOperation.EPS_DEBIT_DEPOSIT
                    ? 'Load balance details'
                    : method === EFundsOperation.WALLET_DEBIT_REQUEST ||
                        method === EFundsOperation.E_TRANSFER_DEBIT_REQUEST
                      ? 'Request details'
                      : method === EFundsOperation.PAY_BILL_CREDIT_PAYMENT
                        ? 'Payee details'
                        : 'Payment details',
            rows: [
                {
                    label: `Amount you ${methodText}`,
                    value: `${Number(values.amount_result).toFixed(2)} CAD`,
                    isBold: true
                },
                {
                    label: 'Total fees (included)',
                    value: `${(pointsFeeResult?.fee || 0).toFixed(2)} СAD`,
                    isLoading: isFeeLoading
                },
                {
                    label: isRequest
                        ? 'You receive'
                        : `${isBank ? 'Bank' : isPayBills ? 'Payee' : data?.[method]?.[0]?.value} gets`,
                    value: `${Number(values.amount).toFixed(2)} CAD`,
                    isBold: true
                },
                method !== EFundsOperation.PAY_BILL_CREDIT_PAYMENT && {
                    label: 'Message (optional)',
                    value: values.message
                },
                !!pointsFeeResult?.points &&
                    !isFeeLoading && {
                        label: `Points you'll earn`,
                        value: pointsFeeResult?.points.toFixed(2),
                        isLoading: isFeeLoading
                    }
            ]
        };
    }, [watch()]);

    const getDescription = useMemo(() => {
        switch (method) {
            case EFundsOperation.E_TRANSFER_CREDIT_TRANSFER:
                return 'Please review your payment and recipient details';
            case EFundsOperation.WALLET_CREDIT_TRANSFER:
                return 'Please review your payment and recipient details';
            case EFundsOperation.WALLET_DEBIT_REQUEST:
                return 'Please review your request and recipient details';
            case EFundsOperation.E_TRANSFER_DEBIT_REQUEST:
                return 'Please review your request and recipient details';
            case EFundsOperation.EFT_DEBIT_DEPOSIT:
                return 'Please review your load balance details and make sure the bank account is correct';
            case EFundsOperation.EPS_DEBIT_DEPOSIT:
                return 'Please review your load balance details and make sure the card is correct';
            case EFundsOperation.EFT_CREDIT_WITHDRAWAL:
                return 'Please review your load balance details and make sure the bank account is correct';
            case EFundsOperation.PAY_BILL_CREDIT_PAYMENT:
                return 'Please review your payment and payee details';
                case EFundsOperation.GIFT_CARD_CREDIT_GIFT_CARD:
                return 'Please review your gift card and recipient details'
            default:
                return 'Please review your payment and recipient details';
        }
    }, [method]);

    const getNoteText = useMemo(() => {
        switch (method) {
            case EFundsOperation.WALLET_CREDIT_TRANSFER:
            case EFundsOperation.EFT_CREDIT_WITHDRAWAL:
                return `By clicking “${isSchedule ? 'Schedule payment' : 'Send money'}” below, you agree that you have reviewed and confirmed the information above.`;
            case EFundsOperation.WALLET_DEBIT_REQUEST:
                return `By clicking “${isSchedule ? 'Schedule request' : 'Request money'}” below, you agree that you have reviewed and confirmed the information above.`;
            case EFundsOperation.E_TRANSFER_DEBIT_REQUEST:
                return 'By clicking “Request money” below, you agree that you have reviewed and confirmed the information above.';
            case EFundsOperation.E_TRANSFER_CREDIT_TRANSFER:
                return 'By clicking “Send money” below, you agree that you have reviewed and confirmed the information above.';
            case EFundsOperation.EFT_DEBIT_DEPOSIT:
            case EFundsOperation.EPS_DEBIT_DEPOSIT:
                return `By clicking “${isSchedule ? 'Schedule load balance' : 'Load balance'}” below, you agree that you have reviewed and confirmed the information above.`;
            case EFundsOperation.PAY_BILL_CREDIT_PAYMENT:
                return 'Please note: It may take up to 3 business days for the payment to appear in your biller’s account.';
            case EFundsOperation.GIFT_CARD_CREDIT_GIFT_CARD:
                return 'By clicking “Create gift card” below, you agree that you have reviewed and confirmed the information above.';
            default:
                return '';
        }
    }, [method]);

    const getSubmitText = useMemo(() => {
        switch (method) {
            case EFundsOperation.WALLET_CREDIT_TRANSFER:
            case EFundsOperation.EFT_CREDIT_WITHDRAWAL:
                return isSchedule ? 'Schedule payment' : 'Send money';
            case EFundsOperation.WALLET_DEBIT_REQUEST:
                return isSchedule ? 'Schedule request' : 'Request money';
            case EFundsOperation.E_TRANSFER_DEBIT_REQUEST:
                return 'Request money';
            case EFundsOperation.E_TRANSFER_CREDIT_TRANSFER:
                return 'Send money';
            case EFundsOperation.EFT_DEBIT_DEPOSIT:
            case EFundsOperation.EPS_DEBIT_DEPOSIT:
                return isSchedule ? 'Schedule load balance' : 'Load balance';
            case EFundsOperation.PAY_BILL_CREDIT_PAYMENT:
                return 'Pay bill';
            case EFundsOperation.GIFT_CARD_CREDIT_GIFT_CARD:
                return getValues()?.isBulkGiftCards ? 'Create gift cards' : 'Create gift card';
            default:
                return '';
        }
    }, [method]);

    return (
        <motion.div {...pageAnimation} className={styles.wrapper}>
            <div className={styles.header}>
                <div className={styles.title}>Review and confirm</div>
                <div
                    className={styles.description}
                    dangerouslySetInnerHTML={{ __html: getDescription }}
                />
            </div>
            <div className={styles.summary}>
                {paymentData && (
                    <div className={styles.block}>
                        <Typography variant="body3" fw={600} className={styles.title}>
                            {paymentData.title}
                        </Typography>
                        <div className={styles.content}>
                            {paymentData.rows.map((item: any, i: number) => {
                                if (!item || !item.value) return null;
                                return (
                                    <div className={styles.row} key={i}>
                                        {item.label}{' '}
                                        <span className={item.isBold ? styles.bold : ''}>
                                            {!item.isLoading ? item.value || '-' : ''}
                                            {item.isLoading && (
                                                <Icon
                                                    name="loading_spinner"
                                                    className={styles.spinner}
                                                />
                                            )}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
                <div className={styles.divider} />
                {data?.[method] && (
                    <div className={styles.block}>
                        <Typography variant="body3" fw={600} className={styles.title}>
                            {method === EFundsOperation.EFT_CREDIT_WITHDRAWAL ||
                            method === EFundsOperation.EFT_DEBIT_DEPOSIT
                                ? 'Bank account details'
                                : method === EFundsOperation.EPS_DEBIT_DEPOSIT
                                  ? 'Card details'
                                  : method === EFundsOperation.WALLET_DEBIT_REQUEST ||
                                      method === EFundsOperation.E_TRANSFER_DEBIT_REQUEST
                                    ? `Contact's info`
                                    : getValues()?.isBulkGiftCards
                                      ? 'Recipients details'
                                      : 'Recipient details'}
                        </Typography>
                        <div className={styles.content}>
                            {data?.[method]?.map((item: any, i: number) => {
                                if (!item || !item.value) return null;
                                return (
                                    <div className={styles.row} key={i}>
                                        {item.label}{' '}
                                        <span className={item.isBold ? styles.bold : ''}>
                                            {item.value || '-'}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
                {isSchedule && <div className={styles.divider} />}
                {isSchedule && scheduleData && (
                    <div className={styles.block}>
                        <Typography variant="body3" fw={600} className={styles.title}>
                            {scheduleData.title}
                        </Typography>
                        <div className={styles.content}>
                            {scheduleData.rows.map((item: any, i: number) => {
                                if (!item || !item.value) return null;
                                return (
                                    <div className={styles.row} key={i}>
                                        {item.label}{' '}
                                        <span className={item.isBold ? styles.bold : ''}>
                                            {item.value || '-'}{' '}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>
            <InfoBlock variant="gray">
                {(method === EFundsOperation.EFT_CREDIT_WITHDRAWAL ||
                    method === EFundsOperation.EFT_DEBIT_DEPOSIT) && (
                    <span style={{ marginBottom: '8px', display: 'block' }}>
                        Please note: Bank transfer times may vary and it can take up to 3 business
                        days for the funds to appear in your wallet.
                    </span>
                )}
                {getNoteText}
            </InfoBlock>
            <div className={styles.actions}>
                <Button leftIcon="arrow_left" variant="outlined-primary" onClick={onPrev}>
                    {submitLocale('previous')}
                </Button>
                <Button variant="primary" onClick={onSubmit} isLoading={isLoading}>
                    {isEdit ? submitLocale('update_schedule') : getSubmitText}
                </Button>
            </div>
        </motion.div>
    );
};

export default Summary;
